/**
 * MouseSettingsPanel — Small overlay (toggled with the `O` key) that lets the
 * player tune mouse look feel: pointer sensitivity and pitch inversion.
 * Values persist to localStorage so the setup carries across reloads.
 *
 * Constructed once and parented to the HUD root. The panel is position:absolute
 * so it floats over the play area without disturbing the HUD layout.
 */
import { clamp } from '../../shared/utils';

export interface MouseSettings {
  sensitivity: number;
  invertPitch: boolean;
}

export const MOUSE_SETTINGS_STORAGE_KEY = 'gameDrone.mouseSettings';

const DEFAULT_SETTINGS: MouseSettings = {
  sensitivity: 1.0,
  invertPitch: false,
};

const SLIDER_MIN = 0.5;
const SLIDER_MAX = 2.5;
const SLIDER_STEP = 0.1;

/**
 * Read persisted settings from localStorage. Falls back to defaults if the
 * key is missing or the stored JSON is malformed/partial — the panel never
 * crashes the game over a corrupted preference blob.
 */
function loadSettings(): MouseSettings {
  if (typeof localStorage === 'undefined') return { ...DEFAULT_SETTINGS };
  try {
    const raw = localStorage.getItem(MOUSE_SETTINGS_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const parsed = JSON.parse(raw) as Partial<MouseSettings>;
    return {
      sensitivity:
        typeof parsed.sensitivity === 'number' && Number.isFinite(parsed.sensitivity)
          ? clamp(parsed.sensitivity, SLIDER_MIN, SLIDER_MAX)
          : DEFAULT_SETTINGS.sensitivity,
      invertPitch:
        typeof parsed.invertPitch === 'boolean'
          ? parsed.invertPitch
          : DEFAULT_SETTINGS.invertPitch,
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

function saveSettings(s: MouseSettings): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(MOUSE_SETTINGS_STORAGE_KEY, JSON.stringify(s));
  } catch {
    // Storage quota or disabled — ignore; the in-memory value still applies.
  }
}

export class MouseSettingsPanel {
  private panel: HTMLElement;
  private settings: MouseSettings;
  private changeCb: ((s: MouseSettings) => void) | null = null;
  private keyHandler: (e: KeyboardEvent) => void;

  constructor(private hudRoot: HTMLElement) {
    this.settings = loadSettings();

    this.panel = document.createElement('div');
    this.panel.className = 'mouse-settings-panel';
    this.panel.style.cssText = `
      position: absolute;
      top: 60px;
      right: 20px;
      width: 280px;
      padding: 16px;
      background: rgba(10, 10, 26, 0.92);
      border: 1px solid rgba(120, 220, 255, 0.4);
      border-radius: 6px;
      color: white;
      font: 13px system-ui, sans-serif;
      pointer-events: auto;
      display: none;
      z-index: 900;
    `;

    const title = document.createElement('div');
    title.textContent = 'Mouse Settings (press ` to close)';
    title.style.cssText = 'font-weight: 600; margin-bottom: 12px; color: rgba(120, 220, 255, 1);';
    this.panel.appendChild(title);

    this.panel.appendChild(
      this.buildSliderRow('Mouse Sensitivity', this.settings.sensitivity, (v) => {
        this.settings.sensitivity = v;
        this.persistAndNotify();
      }),
    );

    this.panel.appendChild(this.buildInvertRow());

    this.hudRoot.appendChild(this.panel);

    // Window-level so the toggle works regardless of focus. We deliberately
    // skip the listener while the user is typing in an input/textarea so a
    // legitimate ` character in the username field doesn't pop the panel.
    // `Backquote` (e.code) is the physical key under Esc on every layout —
    // chosen because KeyO is already bound to ability slot 2.
    this.keyHandler = (e) => {
      if (e.code !== 'Backquote') return;
      const t = e.target as HTMLElement | null;
      if (t) {
        const tag = t.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA') return;
        if (t.isContentEditable) return;
      }
      this.toggle();
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', this.keyHandler);
    }
  }

  /** Build a labeled slider row and wire its `input` event. */
  private buildSliderRow(
    label: string,
    initialValue: number,
    onChange: (v: number) => void,
  ): HTMLElement {
    const row = document.createElement('div');
    row.style.cssText = 'margin-bottom: 12px;';

    const header = document.createElement('div');
    header.style.cssText = 'display: flex; justify-content: space-between; margin-bottom: 4px;';

    const labelEl = document.createElement('span');
    labelEl.textContent = label;
    header.appendChild(labelEl);

    const valueEl = document.createElement('span');
    valueEl.textContent = initialValue.toFixed(1);
    valueEl.style.cssText = 'color: rgba(120, 220, 255, 1); font-variant-numeric: tabular-nums;';
    header.appendChild(valueEl);

    row.appendChild(header);

    const input = document.createElement('input');
    input.type = 'range';
    input.min = String(SLIDER_MIN);
    input.max = String(SLIDER_MAX);
    input.step = String(SLIDER_STEP);
    input.value = String(initialValue);
    input.style.cssText = 'width: 100%; accent-color: rgba(120, 220, 255, 1);';
    input.addEventListener('input', () => {
      const v = clamp(parseFloat(input.value) || 1.0, SLIDER_MIN, SLIDER_MAX);
      valueEl.textContent = v.toFixed(1);
      onChange(v);
    });
    row.appendChild(input);

    return row;
  }

  private buildInvertRow(): HTMLElement {
    const row = document.createElement('label');
    row.style.cssText = 'display: flex; align-items: center; gap: 8px; cursor: pointer; user-select: none;';

    const input = document.createElement('input');
    input.type = 'checkbox';
    input.checked = this.settings.invertPitch;
    input.addEventListener('change', () => {
      this.settings.invertPitch = input.checked;
      this.persistAndNotify();
    });
    row.appendChild(input);

    const text = document.createElement('span');
    text.textContent = 'Invert Pitch';
    row.appendChild(text);

    return row;
  }

  private persistAndNotify(): void {
    saveSettings(this.settings);
    this.changeCb?.({ ...this.settings });
  }

  /** Current pointer sensitivity (0.5–2.5; default 1.0). */
  getSensitivity(): number {
    return this.settings.sensitivity;
  }

  /** Whether vertical look should be inverted. */
  getInvertPitch(): boolean {
    return this.settings.invertPitch;
  }

  /**
   * Subscribe to settings changes. The callback fires on every slider /
   * checkbox edit with the full settings snapshot — consumers don't have to
   * track which control moved.
   */
  onChange(cb: (settings: MouseSettings) => void): void {
    this.changeCb = cb;
  }

  show(): void {
    this.panel.style.display = 'block';
  }

  hide(): void {
    this.panel.style.display = 'none';
  }

  toggle(): void {
    if (this.panel.style.display === 'none' || this.panel.style.display === '') {
      this.show();
    } else {
      this.hide();
    }
  }

  dispose(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.keyHandler);
    }
    if (this.panel.parentElement) {
      this.panel.parentElement.removeChild(this.panel);
    }
  }
}
